import React, { useState, useRef, useEffect } from "react";
import {
  Calendar,
  MapPin,
  Heart,
  Sparkles,
  Navigation,
  Plus,
  Minus,
  Maximize2,
  Clock,
  Star,
  DollarSign,
  ChevronRight,
  X,
  Check,
  TrendingUp,
  Users,
  Utensils,
  Trees,
  Mountain,
  ShoppingBag,
  Building,
  Train,
  Send,
  Coffee,
  Camera,
} from "lucide-react";

const TripPlanner = () => {
  const [activeTab, setActiveTab] = useState("ai");
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);

  // Chat state
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "ai",
      text: "Hi! I'm CityGetAway's AI Travel Planner. I'll help you create the perfect Montreal itinerary through smart conversation!",
      time: new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);
  const [currentStep, setCurrentStep] = useState("welcome");
  const [tripData, setTripData] = useState({
    days: null,
    people: null,
    budget: null,
    interests: [],
  });
  const [interestStep, setInterestStep] = useState(1); // Track interest selection step

  // Map locations for Montreal
  const mapLocations = [
    {
      id: 1,
      type: "attraction",
      name: "Old Montreal",
      icon: Building,
      top: "40%",
      left: "50%",
      rating: 4.9,
      price: "Free",
      hours: "Always Open",
      description:
        "Historic district with cobblestone streets, 17th-century architecture, and charming boutiques.",
    },
    {
      id: 2,
      type: "restaurant",
      name: "Schwartz's Deli",
      icon: Utensils,
      top: "35%",
      left: "45%",
      rating: 4.7,
      price: "$15-25",
      hours: "8:00-24:00",
      description:
        "Legendary Montreal smoked meat since 1928. Must-try local delicacy.",
    },
    {
      id: 3,
      type: "park",
      name: "Mount Royal Park",
      icon: Trees,
      top: "30%",
      left: "40%",
      rating: 4.8,
      price: "Free",
      hours: "6:00-24:00",
      description:
        "Designed by Frederick Law Olmsted, offering stunning city views and nature trails.",
    },
    {
      id: 4,
      type: "shopping",
      name: "Underground City",
      icon: ShoppingBag,
      top: "50%",
      left: "55%",
      rating: 4.5,
      price: "Free entry",
      hours: "10:00-21:00",
      description:
        "32km of underground tunnels with shops, restaurants, and metro connections.",
    },
    {
      id: 5,
      type: "attraction",
      name: "Notre-Dame Basilica",
      icon: Building,
      top: "45%",
      left: "48%",
      rating: 4.8,
      price: "$15",
      hours: "9:00-16:30",
      description:
        "Gothic Revival masterpiece with stunning stained glass and light shows.",
    },
    {
      id: 6,
      type: "cafe",
      name: "Café Olimpico",
      icon: Coffee,
      top: "25%",
      left: "35%",
      rating: 4.6,
      price: "$5-10",
      hours: "7:00-23:00",
      description:
        "Mile End's iconic Italian café, perfect for people watching.",
    },
  ];

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Update trip data when interests change
  useEffect(() => {
    setTripData((prev) => ({ ...prev, interests: selectedInterests }));
  }, [selectedInterests]);

  // Handle quick replies
  const handleQuickReply = (reply, step) => {
    const userMessage = {
      id: messages.length + 1,
      type: "user",
      text: reply,
      time: new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);

    setTimeout(() => {
      let aiResponse = {};
      let newStep = currentStep;
      let newTripData = { ...tripData };

      switch (step) {
        case "start":
          aiResponse = {
            id: messages.length + 2,
            type: "ai",
            text: "Great! Let me understand your travel plans better.\n\nFirst, how many days are you planning to stay in Montreal?",
            quickReplies: [
              "1 Day",
              "2 Days",
              "3 Days",
              "4 Days",
              "5 Days",
              "1 Week",
            ],
            replyStep: "days",
          };
          newStep = "days";
          break;

        case "days":
          newTripData.days = reply;
          aiResponse = {
            id: messages.length + 2,
            type: "ai",
            text: `Perfect! ${reply} in Montreal will be amazing. How many people will be traveling?`,
            quickReplies: [
              "Solo Travel",
              "2 People",
              "3-4 People",
              "5+ People",
              "Family Trip",
            ],
            replyStep: "people",
          };
          newStep = "people";
          break;

        case "people":
          newTripData.people = reply;
          aiResponse = {
            id: messages.length + 2,
            type: "ai",
            text: `Excellent! ${
              reply === "Solo Travel"
                ? "Solo travel offers great flexibility"
                : `Traveling with ${reply} will be fun`
            }. What's your budget preference?`,
            quickReplies: ["Budget-Friendly", "Mid-Range", "Luxury"],
            replyStep: "budget",
          };
          newStep = "budget";
          break;

        case "budget":
          newTripData.budget = reply;
          aiResponse = {
            id: messages.length + 2,
            type: "ai",
            text: "Got it! Now let's customize your interests.\n\nPlease switch to the 'Interests' tab to select activities you enjoy, then come back here or check your personalized itinerary!",
            showTabButtons: true,
          };
          newStep = "interests";
          break;

        default:
          break;
      }

      aiResponse.time = new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
      setMessages((prev) => [...prev, aiResponse]);
      setCurrentStep(newStep);
      setTripData(newTripData);
    }, 500);
  };

  // Generate itinerary based on selections
  const generateItinerary = () => {
    const items = [];
    const { days, budget, interests } = tripData;

    // Base attractions
    items.push({
      icon: Building,
      title: "Old Montreal",
      subtitle: "Historic district walk",
      time: "Day 1 Morning",
      duration: "3 hours",
      color: "rgba(240, 249, 255, 0.7)",
    });

    // Add based on interests
    if (interests.includes(1)) {
      // Street Food
      items.push({
        icon: Utensils,
        title: "Jean-Talon Market",
        subtitle: "Local food tour",
        time: "Day 1 Lunch",
        duration: "2 hours",
        color: "rgba(255, 243, 224, 0.7)",
      });
    }

    if (interests.includes(2)) {
      // History
      items.push({
        icon: Building,
        title: "Notre-Dame Basilica",
        subtitle: "Gothic architecture",
        time: "Day 1 Afternoon",
        duration: "1.5 hours",
        color: "rgba(240, 249, 255, 0.7)",
      });
    }

    if (interests.includes(3)) {
      // Art
      items.push({
        icon: Camera,
        title: "Museum of Fine Arts",
        subtitle: "Art collections",
        time: "Day 2 Morning",
        duration: "2.5 hours",
        color: "rgba(254, 242, 242, 0.7)",
      });
    }

    if (interests.includes(4)) {
      // Nature
      items.push({
        icon: Trees,
        title: "Mount Royal Park",
        subtitle: "Scenic hike & views",
        time: "Day 2 Afternoon",
        duration: "3 hours",
        color: "rgba(236, 253, 245, 0.7)",
      });
    }

    if (interests.includes(5)) {
      // Shopping
      items.push({
        icon: ShoppingBag,
        title: "Underground City",
        subtitle:
          budget === "Luxury" ? "Designer shopping" : "Shopping & dining",
        time: "Day 2 Evening",
        duration: "2 hours",
        color: "rgba(255, 251, 235, 0.7)",
      });
    }

    if (interests.includes(6)) {
      // Culture
      items.push({
        icon: Coffee,
        title: "Mile End District",
        subtitle: "Cafés & culture",
        time: "Day 3 Morning",
        duration: "2 hours",
        color: "rgba(240, 253, 252, 0.7)",
      });
    }

    // Budget-specific additions
    if (budget === "Budget-Friendly") {
      items.push({
        icon: Trees,
        title: "Lachine Canal",
        subtitle: "Free bike path",
        time: "Any afternoon",
        duration: "2 hours",
        color: "rgba(236, 253, 245, 0.7)",
      });
    } else if (budget === "Luxury") {
      items.push({
        icon: Utensils,
        title: "Toqué! Restaurant",
        subtitle: "Fine dining experience",
        time: "Special evening",
        duration: "3 hours",
        color: "rgba(254, 242, 242, 0.7)",
      });
    }

    return items.slice(0, 6); // Return max 6 items
  };

  // Handle send message
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: "user",
      text: inputValue,
      time: new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    setTimeout(() => {
      const aiResponse = {
        id: messages.length + 2,
        type: "ai",
        text: "I understand! Let me help you with that. Feel free to explore the other tabs to see the map and your personalized itinerary!",
        time: new Date().toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, aiResponse]);
    }, 500);
  };

  // Inline styles
  const styles = {
    container: {
      width: "100%",
      maxWidth: "430px",
      height: "100vh",
      margin: "0 auto",
      background: "#f8f8f8",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    },
    bgDecoration: {
      position: "absolute",
      width: "250px",
      height: "250px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #E3E3E3 0%, #EAEAEA 100%)",
      opacity: "0.4",
      zIndex: 0,
    },
    bgDecoration1: {
      top: "-100px",
      right: "-80px",
    },
    bgDecoration2: {
      bottom: "100px",
      left: "-100px",
    },
    header: {
      background: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      padding: "50px 24px 20px",
      borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
      position: "relative",
      zIndex: 10,
    },
    headerTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    backButton: {
      background: "#f0f0f0",
      border: "none",
      borderRadius: "10px",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#6a6a6a",
    },
    headerTitle: {
      fontSize: "20px",
      fontWeight: "300",
      color: "#1a1a1a",
      margin: 0,
    },
    headerSubtitle: {
      fontSize: "14px",
      color: "#7a7a7a",
      fontWeight: "400",
    },
    tabContainer: {
      display: "flex",
      gap: "8px",
      padding: "0 20px 16px",
      background: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      position: "relative",
      zIndex: 10,
    },
    tab: (isActive) => ({
      flex: 1,
      padding: "10px 8px",
      background: isActive ? "#2d2d2d" : "#f0f0f0",
      border: "none",
      borderRadius: "10px",
      color: isActive ? "white" : "#6a6a6a",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s",
    }),
    content: {
      flex: 1,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      position: "relative",
      zIndex: 1,
      padding: "20px",
      display: "flex",
      flexDirection: "column",
    },
    // Chat styles
    chatSection: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },
    messagesContainer: {
      flex: 1,
      overflowY: "auto",
      paddingBottom: "20px",
    },
    chatMessage: (type) => ({
      display: "flex",
      gap: "8px",
      marginBottom: "16px",
      alignItems: "flex-start",
      flexDirection: type === "user" ? "row-reverse" : "row",
    }),
    messageAvatar: (type) => ({
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: type === "ai" ? "#3b82f6" : "#667eea",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      flexShrink: 0,
    }),
    messageBubble: (type) => ({
      maxWidth: "75%",
      padding: "12px",
      borderRadius: "12px",
      background: type === "ai" ? "white" : "#3b82f6",
      color: type === "ai" ? "#2d2d2d" : "white",
      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    }),
    messageHeader: {
      fontSize: "12px",
      fontWeight: "600",
      marginBottom: "4px",
      color: "#6a6a6a",
    },
    messageText: {
      fontSize: "14px",
      lineHeight: "1.5",
      whiteSpace: "pre-wrap",
    },
    messageTime: {
      fontSize: "11px",
      marginTop: "8px",
      opacity: "0.7",
    },
    quickReplies: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      marginTop: "12px",
    },
    quickReplyButton: {
      padding: "8px 12px",
      background: "#f0f0f0",
      border: "none",
      borderRadius: "8px",
      fontSize: "12px",
      color: "#2d2d2d",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    tabButton: {
      flex: 1,
      padding: "10px",
      background: "#667eea",
      border: "none",
      borderRadius: "8px",
      color: "white",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      marginTop: "12px",
    },
    inputArea: {
      display: "flex",
      gap: "8px",
      padding: "12px 0",
      borderTop: "1px solid #e5e7eb",
    },
    messageInput: {
      flex: 1,
      padding: "12px",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "14px",
      outline: "none",
      background: "white",
    },
    sendButton: {
      width: "44px",
      height: "44px",
      borderRadius: "12px",
      background: "#3b82f6",
      border: "none",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
    startButton: {
      width: "100%",
      padding: "14px",
      background: "#2d2d2d",
      border: "none",
      borderRadius: "12px",
      color: "white",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s",
      marginTop: "12px",
    },
    progressSection: {
      marginBottom: "20px",
    },
    progressHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    progressTitle: {
      fontSize: "15px",
      color: "#2d2d2d",
      fontWeight: "500",
    },
    progressText: {
      fontSize: "13px",
      color: "#7a7a7a",
    },
    progressBar: {
      background: "#f0f0f0",
      height: "6px",
      borderRadius: "3px",
      overflow: "hidden",
    },
    progressFill: (percent) => ({
      background: "#667eea",
      height: "100%",
      width: `${percent}%`,
      transition: "width 0.3s",
      borderRadius: "3px",
    }),
    interestsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginBottom: "20px",
    },
    interestCategoriesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "12px",
      marginBottom: "20px",
    },
    categoryCard: {
      borderRadius: "16px",
      border: "1px solid rgba(0,0,0,0.08)",
      padding: "16px",
      minHeight: "140px",
      cursor: "pointer",
      transition: "all 0.2s",
      position: "relative",
    },
    categoryIcon: (isSelected) => ({
      width: "36px",
      height: "36px",
      borderRadius: "8px",
      background: isSelected ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.05)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "8px",
      color: isSelected ? "white" : "#6a6a6a",
    }),
    categoryTitle: {
      fontSize: "13px",
      fontWeight: "600",
      marginBottom: "4px",
    },
    categoryDesc: (isSelected) => ({
      fontSize: "11px",
      marginBottom: "8px",
      opacity: isSelected ? 0.9 : 0.7,
    }),
    categoryStats: (isSelected) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "10px",
      marginBottom: "8px",
      opacity: isSelected ? 0.9 : 0.6,
    }),
    categoryActions: {
      display: "flex",
      gap: "6px",
    },
    selectButton: {
      padding: "4px 8px",
      background: "rgba(0,0,0,0.05)",
      border: "none",
      borderRadius: "6px",
      fontSize: "10px",
      cursor: "pointer",
    },
    selectedButton: {
      padding: "4px 8px",
      background: "rgba(255,255,255,0.2)",
      border: "none",
      borderRadius: "6px",
      fontSize: "10px",
      color: "white",
      cursor: "pointer",
    },
    activitiesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "12px",
    },
    activityCard: (isSelected) => ({
      background: isSelected ? "#667eea" : "rgba(255,255,255,0.7)",
      borderRadius: "12px",
      border: isSelected ? "none" : "1px solid rgba(0,0,0,0.08)",
      padding: "16px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
    }),
    activityTitle: (isSelected) => ({
      fontSize: "13px",
      fontWeight: "600",
      color: isSelected ? "white" : "#2d2d2d",
      marginTop: "8px",
      marginBottom: "4px",
    }),
    activityDesc: (isSelected) => ({
      fontSize: "11px",
      color: isSelected ? "rgba(255,255,255,0.8)" : "#7a7a7a",
      marginBottom: "8px",
    }),
    activityPopularity: (isSelected) => ({
      fontSize: "10px",
      color: isSelected ? "rgba(255,255,255,0.7)" : "#9a9a9a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
    }),
    continueButton: {
      width: "100%",
      padding: "14px",
      background: "#667eea",
      border: "none",
      borderRadius: "12px",
      color: "white",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      flex: 1,
    },
    backButton2: {
      padding: "14px",
      background: "#f0f0f0",
      border: "none",
      borderRadius: "12px",
      color: "#2d2d2d",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      minWidth: "100px",
    },
    recommendationsList: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginBottom: "20px",
    },
    recommendationCard: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px",
      background: "white",
      borderRadius: "12px",
      fontSize: "13px",
      color: "#2d2d2d",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    interestCard: (isSelected) => ({
      background: isSelected ? "#667eea" : "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(10px)",
      border: isSelected ? "none" : "1px solid rgba(255, 255, 255, 0.8)",
      borderRadius: "12px",
      padding: "16px 8px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
    }),
    interestIcon: (isSelected) => ({
      fontSize: "20px",
      marginBottom: "8px",
      color: isSelected ? "white" : "#6a6a6a",
    }),
    interestLabel: (isSelected) => ({
      fontSize: "11px",
      fontWeight: "500",
      color: isSelected ? "white" : "#6a6a6a",
    }),
    mapContainer: {
      background: "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(10px)",
      borderRadius: "16px",
      border: "1px solid rgba(255, 255, 255, 0.8)",
      padding: "16px",
      marginBottom: "16px",
    },
    mapHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    mapTitle: {
      fontSize: "15px",
      fontWeight: "500",
      color: "#2d2d2d",
    },
    mapView: {
      width: "100%",
      height: "320px",
      background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
      borderRadius: "12px",
      position: "relative",
      overflow: "hidden",
    },
    mapControls: {
      position: "absolute",
      right: "12px",
      top: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      zIndex: 10,
    },
    mapButton: {
      width: "32px",
      height: "32px",
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#6a6a6a",
    },
    mapMarker: (type) => ({
      position: "absolute",
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      transition: "transform 0.2s",
      background:
        type === "attraction"
          ? "#667eea"
          : type === "restaurant"
          ? "#f97316"
          : type === "park"
          ? "#10b981"
          : type === "shopping"
          ? "#ec4899"
          : type === "cafe"
          ? "#8b5cf6"
          : "#6b7280",
    }),
    mapLegend: {
      display: "flex",
      gap: "12px",
      marginTop: "12px",
      flexWrap: "wrap",
      fontSize: "11px",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      color: "#6a6a6a",
    },
    legendDot: (color) => ({
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: color,
    }),
    itineraryGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "12px",
    },
    itineraryCard: (color) => ({
      background: color,
      borderRadius: "16px",
      border: "1px solid rgba(255, 255, 255, 0.8)",
      padding: "16px",
      minHeight: "120px",
      cursor: "pointer",
      transition: "all 0.2s",
    }),
    dayCard: {
      background: "white",
      borderRadius: "16px",
      border: "1px solid rgba(0,0,0,0.08)",
      marginBottom: "12px",
      overflow: "hidden",
    },
    dayHeader: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 16px",
      background: "#f8f9fa",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
    },
    dayTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#2d2d2d",
      flex: 1,
    },
    dayLocations: {
      fontSize: "12px",
      color: "#7a7a7a",
    },
    dayActivities: {
      padding: "8px",
    },
    activityItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px",
      borderRadius: "12px",
      marginBottom: "8px",
      cursor: "pointer",
      transition: "background 0.2s",
    },
    activityNumber: {
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      background: "#667eea",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "600",
      flexShrink: 0,
    },
    activityDetails: {
      flex: 1,
    },
    activityName: {
      fontSize: "13px",
      fontWeight: "500",
      color: "#2d2d2d",
      marginBottom: "2px",
    },
    activityTime: {
      fontSize: "11px",
      color: "#7a7a7a",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    aiTipsCard: {
      background: "#fff9c4",
      borderRadius: "16px",
      padding: "16px",
      marginBottom: "20px",
    },
    saveButton: {
      flex: 1,
      padding: "14px",
      background: "#2d2d2d",
      border: "none",
      borderRadius: "12px",
      color: "white",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
    },
    shareButton: {
      flex: 1,
      padding: "14px",
      background: "#f0f0f0",
      border: "none",
      borderRadius: "12px",
      color: "#2d2d2d",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
    },
    cardIcon: {
      width: "32px",
      height: "32px",
      background: "rgba(255, 255, 255, 0.5)",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "12px",
      color: "#6a6a6a",
    },
    cardTitle: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#2d2d2d",
      marginBottom: "4px",
    },
    cardSubtitle: {
      fontSize: "11px",
      color: "#7a7a7a",
      marginBottom: "8px",
    },
    cardTime: {
      fontSize: "10px",
      color: "#9a9a9a",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    locationModal: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      background: "white",
      borderRadius: "20px 20px 0 0",
      padding: "20px",
      boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
      zIndex: 1000,
      maxWidth: "430px",
      margin: "0 auto",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    modalTitle: {
      fontSize: "16px",
      fontWeight: "500",
      color: "#2d2d2d",
    },
    modalClose: {
      background: "none",
      border: "none",
      fontSize: "24px",
      color: "#6a6a6a",
      cursor: "pointer",
    },
    modalDescription: {
      fontSize: "13px",
      color: "#7a7a7a",
      lineHeight: "1.5",
      marginBottom: "12px",
    },
    modalStats: {
      display: "flex",
      gap: "16px",
      marginBottom: "16px",
    },
    statItem: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "12px",
      color: "#6a6a6a",
    },
    modalActions: {
      display: "flex",
      gap: "12px",
    },
    modalButton: (isPrimary) => ({
      flex: 1,
      padding: "12px",
      borderRadius: "12px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      background: isPrimary ? "#2d2d2d" : "#f0f0f0",
      color: isPrimary ? "white" : "#2d2d2d",
    }),
  };

  const interests = [
    { id: 1, icon: Utensils, label: "Street Food" },
    { id: 2, icon: Building, label: "History" },
    { id: 3, icon: Camera, label: "Art" },
    { id: 4, icon: Trees, label: "Nature" },
    { id: 5, icon: ShoppingBag, label: "Shopping" },
    { id: 6, icon: Coffee, label: "Culture" },
  ];

  const toggleInterest = (id) => {
    setSelectedInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setShowLocationDetails(true);
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.bgDecoration, ...styles.bgDecoration1 }}></div>
      <div style={{ ...styles.bgDecoration, ...styles.bgDecoration2 }}></div>

      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button
            style={styles.backButton}
            onClick={() => window.history.back()}
          >
            <ChevronRight
              style={{ transform: "rotate(180deg)", width: 20, height: 20 }}
            />
          </button>
          <div className="header-logo">
            <img
              src="/assets/logocity.jpg"
              alt="Logo"
              className="logo-img"
              style={{
                width: "24px",
                height: "24px",
                objectFit: "contain",
                borderRadius: "6px",
              }}
            />
            <span className="logo-text">Concord Ai Assistant</span>
          </div>
          <button style={styles.backButton}>
            <Calendar style={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>

      <div style={styles.tabContainer}>
        <button
          style={styles.tab(activeTab === "ai")}
          onClick={() => setActiveTab("ai")}
        >
          AI Chat
        </button>
        <button
          style={styles.tab(activeTab === "interests")}
          onClick={() => setActiveTab("interests")}
        >
          Interests
        </button>
        <button
          style={styles.tab(activeTab === "map")}
          onClick={() => setActiveTab("map")}
        >
          Map View
        </button>
        <button
          style={styles.tab(activeTab === "itinerary")}
          onClick={() => setActiveTab("itinerary")}
        >
          Itinerary
        </button>
      </div>

      <div style={styles.content}>
        {activeTab === "ai" && (
          <div style={styles.chatSection}>
            <div style={styles.messagesContainer}>
              {messages.map((message) => (
                <div key={message.id} style={styles.chatMessage(message.type)}>
                  {message.type === "ai" && (
                    <div style={styles.messageAvatar("ai")}>
                      <Sparkles size={16} />
                    </div>
                  )}
                  <div style={styles.messageBubble(message.type)}>
                    {message.type === "ai" && (
                      <div style={styles.messageHeader}>AI Assistant</div>
                    )}
                    <div style={styles.messageText}>{message.text}</div>
                    {message.quickReplies && (
                      <div style={styles.quickReplies}>
                        {message.quickReplies.map((reply) => (
                          <button
                            key={reply}
                            style={styles.quickReplyButton}
                            onClick={() =>
                              handleQuickReply(reply, message.replyStep)
                            }
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                    {message.showTabButtons && (
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          style={styles.tabButton}
                          onClick={() => setActiveTab("interests")}
                        >
                          Select Interests
                        </button>
                        <button
                          style={styles.tabButton}
                          onClick={() => setActiveTab("itinerary")}
                        >
                          View Itinerary
                        </button>
                      </div>
                    )}
                    <div style={styles.messageTime}>{message.time}</div>
                  </div>
                </div>
              ))}
              {currentStep === "welcome" && messages.length === 1 && (
                <div style={styles.chatMessage("ai")}>
                  <div style={styles.messageAvatar("ai")}>
                    <Sparkles size={16} />
                  </div>
                  <div style={styles.messageBubble("ai")}>
                    <div style={styles.quickReplies}>
                      <button
                        style={styles.quickReplyButton}
                        onClick={() =>
                          handleQuickReply("Start planning my trip", "start")
                        }
                      >
                        Start planning my trip
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={styles.inputArea}>
              <input
                type="text"
                placeholder="Type your message..."
                style={styles.messageInput}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <button style={styles.sendButton} onClick={handleSendMessage}>
                <Send size={20} />
              </button>
            </div>
          </div>
        )}

        {activeTab === "interests" && (
          <div>
            {/* Progress header */}
            <div style={styles.progressSection}>
              <div style={styles.progressHeader}>
                <h3 style={styles.progressTitle}>
                  {interestStep === 1 && "Choose Your Interests"}
                  {interestStep === 2 && "Select Activities"}
                  {interestStep === 3 && "AI Recommendations"}
                </h3>
                <span style={styles.progressText}>
                  {interestStep < 3
                    ? `Step ${interestStep}/2`
                    : `${selectedInterests.length} interests selected`}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={styles.progressFill((interestStep / 3) * 100)}
                ></div>
              </div>
            </div>

            {/* Step 1: Main interest categories */}
            {interestStep === 1 && (
              <>
                <div style={styles.interestCategoriesGrid}>
                  {[
                    {
                      id: "history",
                      icon: Building,
                      title: "History & Heritage",
                      desc: "Museums and historic sites",
                      popularity: "92%",
                      time: "2-4 hours",
                      color: "#fff9c4",
                    },
                    {
                      id: "shopping",
                      icon: ShoppingBag,
                      title: "Shopping Experience",
                      desc: "Boutiques and markets",
                      popularity: "78%",
                      time: "1-3 hours",
                      color: "#fce4ec",
                    },
                    {
                      id: "nature",
                      icon: Trees,
                      title: "Parks & Gardens",
                      desc: "Green spaces in the city",
                      popularity: "83%",
                      time: "1-3 hours",
                      color: "#e8f5e9",
                    },
                    {
                      id: "scenic",
                      icon: Mountain,
                      title: "Scenic Views",
                      desc: "Best photo spots",
                      popularity: "87%",
                      time: "2-4 hours",
                      color: "#e3f2fd",
                    },
                    {
                      id: "dining",
                      icon: Utensils,
                      title: "Local Dining",
                      desc: "Must-try restaurants",
                      popularity: "96%",
                      time: "1-2 hours",
                      color: "#ffe4e1",
                    },
                    {
                      id: "nightlife",
                      icon: Coffee,
                      title: "Nightlife",
                      desc: "Experience the night scene",
                      popularity: "76%",
                      time: "2-4 hours",
                      color: "#f3e5f5",
                    },
                  ].map((category) => (
                    <div
                      key={category.id}
                      style={{
                        ...styles.categoryCard,
                        background: selectedInterests.includes(category.id)
                          ? "#667eea"
                          : category.color,
                        color: selectedInterests.includes(category.id)
                          ? "white"
                          : "#2d2d2d",
                        borderColor: selectedInterests.includes(category.id)
                          ? "#667eea"
                          : "transparent",
                      }}
                      onClick={() => toggleInterest(category.id)}
                    >
                      <div
                        style={styles.categoryIcon(
                          selectedInterests.includes(category.id)
                        )}
                      >
                        <category.icon size={20} />
                      </div>
                      <div style={styles.categoryTitle}>{category.title}</div>
                      <div
                        style={styles.categoryDesc(
                          selectedInterests.includes(category.id)
                        )}
                      >
                        {category.desc}
                      </div>
                      <div
                        style={styles.categoryStats(
                          selectedInterests.includes(category.id)
                        )}
                      >
                        <Star size={12} /> {category.popularity} •{" "}
                        <Clock size={12} /> {category.time}
                      </div>
                      <div style={styles.categoryActions}>
                        {selectedInterests.includes(category.id) ? (
                          <button style={styles.selectedButton}>
                            Selected ✓
                          </button>
                        ) : (
                          <button style={styles.selectButton}>Select</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedInterests.length > 0 && (
                  <button
                    style={styles.continueButton}
                    onClick={() => setInterestStep(2)}
                  >
                    Continue to Activities →
                  </button>
                )}
              </>
            )}

            {/* Step 2: Specific activities */}
            {interestStep === 2 && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <h4
                    style={{
                      fontSize: "14px",
                      color: "#2d2d2d",
                      marginBottom: "8px",
                    }}
                  >
                    Select specific activities you'd like to include:
                  </h4>
                </div>
                <div style={styles.activitiesGrid}>
                  {[
                    {
                      id: "wellness",
                      icon: Heart,
                      title: "Wellness",
                      desc: "Spa and relaxation",
                      popularity: "68%",
                    },
                    {
                      id: "social",
                      icon: Users,
                      title: "Social Gathering",
                      desc: "Meet locals and travelers",
                      popularity: "82%",
                    },
                    {
                      id: "learning",
                      icon: Building,
                      title: "Learning Experience",
                      desc: "Workshops and classes",
                      popularity: "63%",
                    },
                    {
                      id: "photography",
                      icon: Camera,
                      title: "Photography Spots",
                      desc: "Best photo locations",
                      popularity: "94%",
                    },
                  ].map((activity) => {
                    const isSelected = selectedInterests.includes(activity.id);
                    return (
                      <div
                        key={activity.id}
                        style={styles.activityCard(isSelected)}
                        onClick={() => toggleInterest(activity.id)}
                      >
                        <activity.icon
                          size={18}
                          style={{ color: isSelected ? "white" : "#6a6a6a" }}
                        />
                        <div style={styles.activityTitle(isSelected)}>
                          {activity.title}
                        </div>
                        <div style={styles.activityDesc(isSelected)}>
                          {activity.desc}
                        </div>
                        <div style={styles.activityPopularity(isSelected)}>
                          <Star size={10} /> {activity.popularity}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{ display: "flex", gap: "12px", marginTop: "20px" }}
                >
                  <button
                    style={styles.backButton2}
                    onClick={() => setInterestStep(1)}
                  >
                    ← Back
                  </button>
                  <button
                    style={styles.continueButton}
                    onClick={() => {
                      setInterestStep(3);
                      // Add AI message about completion
                      setTimeout(() => {
                        const aiMessage = {
                          id: messages.length + 1,
                          type: "ai",
                          text: `Perfect! I've analyzed your preferences and created a personalized itinerary. You've selected ${selectedInterests.length} interests.\n\nBased on your choices, I recommend focusing on:\n• Historical sites in Old Montreal\n• Mount Royal for scenic views\n• Jean-Talon Market for local dining\n\nWould you like me to optimize your route?`,
                          time: new Date().toLocaleTimeString("en-US", {
                            hour12: false,
                            hour: "2-digit",
                            minute: "2-digit",
                          }),
                          showItineraryButton: true,
                        };
                        setMessages((prev) => [...prev, aiMessage]);
                        setCurrentStep("completed");
                      }, 500);
                    }}
                  >
                    Complete Selection →
                  </button>
                </div>
              </>
            )}

            {/* Step 3: AI Recommendations - Selectable */}
            {interestStep === 3 && (
              <div>
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Sparkles size={20} style={{ color: "#667eea" }} />
                    <h3
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#2d2d2d",
                      }}
                    >
                      AI Recommendations
                    </h3>
                  </div>
                  <p style={{ fontSize: "12px", color: "#7a7a7a" }}>
                    Select the recommendations you'd like to include:
                  </p>
                </div>

                {/* Selectable recommendation cards */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    marginBottom: "20px",
                  }}
                >
                  {[
                    {
                      id: "cuisine",
                      icon: Utensils,
                      title: "Local Cuisine",
                      match: "96%",
                      color: "#fff0f0",
                      iconColor: "#ff6b6b",
                    },
                    {
                      id: "historic",
                      icon: Building,
                      title: "Historic Sites",
                      match: "92%",
                      color: "#fff9e6",
                      iconColor: "#ffc107",
                    },
                    {
                      id: "shopping",
                      icon: ShoppingBag,
                      title: "Shopping Experience",
                      match: "91%",
                      color: "#ffe6f0",
                      iconColor: "#ec4899",
                    },
                    {
                      id: "nature",
                      icon: Trees,
                      title: "Parks & Nature",
                      match: "87%",
                      color: "#e8f5e9",
                      iconColor: "#10b981",
                    },
                    {
                      id: "arts",
                      icon: Camera,
                      title: "Arts & Culture",
                      match: "85%",
                      color: "#f3e5f5",
                      iconColor: "#8b5cf6",
                    },
                    {
                      id: "nightlife",
                      icon: Coffee,
                      title: "Cafés & Nightlife",
                      match: "82%",
                      color: "#e3f2fd",
                      iconColor: "#3b82f6",
                    },
                  ].map((rec) => {
                    const Icon = rec.icon;
                    const isSelected = selectedInterests.includes(rec.id);
                    return (
                      <div
                        key={rec.id}
                        style={{
                          background: isSelected ? rec.iconColor : rec.color,
                          borderRadius: "16px",
                          padding: "16px",
                          border: `1px solid ${
                            isSelected ? rec.iconColor : "rgba(0,0,0,0.08)"
                          }`,
                          cursor: "pointer",
                          transition: "all 0.3s",
                        }}
                        onClick={() => toggleInterest(rec.id)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "10px",
                              background: isSelected
                                ? "rgba(255,255,255,0.3)"
                                : "rgba(255,255,255,0.8)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon
                              size={20}
                              style={{
                                color: isSelected ? "white" : rec.iconColor,
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: isSelected ? "white" : "#2d2d2d",
                              }}
                            >
                              {rec.title}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: isSelected
                                  ? "rgba(255,255,255,0.9)"
                                  : "#7a7a7a",
                                marginTop: "2px",
                              }}
                            >
                              <Star
                                size={12}
                                style={{
                                  display: "inline",
                                  color: isSelected ? "white" : "#ffc107",
                                }}
                              />{" "}
                              {rec.match} Match
                            </div>
                          </div>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              background: isSelected
                                ? "rgba(255,255,255,0.3)"
                                : "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {isSelected ? (
                              <Check size={16} style={{ color: "white" }} />
                            ) : (
                              <Plus size={16} style={{ color: "#9a9a9a" }} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected count */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "12px",
                    marginBottom: "16px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#6a6a6a" }}>
                    Selected recommendations
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#2d2d2d",
                    }}
                  >
                    {
                      selectedInterests.filter((id) =>
                        [
                          "cuisine",
                          "historic",
                          "shopping",
                          "nature",
                          "arts",
                          "nightlife",
                        ].includes(id)
                      ).length
                    }{" "}
                    of 6
                  </span>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    style={{
                      ...styles.continueButton,
                      opacity:
                        selectedInterests.filter((id) =>
                          [
                            "cuisine",
                            "historic",
                            "shopping",
                            "nature",
                            "arts",
                            "nightlife",
                          ].includes(id)
                        ).length === 0
                          ? 0.5
                          : 1,
                      cursor:
                        selectedInterests.filter((id) =>
                          [
                            "cuisine",
                            "historic",
                            "shopping",
                            "nature",
                            "arts",
                            "nightlife",
                          ].includes(id)
                        ).length === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={() => {
                      if (
                        selectedInterests.filter((id) =>
                          [
                            "cuisine",
                            "historic",
                            "shopping",
                            "nature",
                            "arts",
                            "nightlife",
                          ].includes(id)
                        ).length > 0
                      ) {
                        // Generate final itinerary based on selected recommendations
                        setActiveTab("itinerary");
                        // Send AI confirmation
                        const selectedRecs = selectedInterests.filter((id) =>
                          [
                            "cuisine",
                            "historic",
                            "shopping",
                            "nature",
                            "arts",
                            "nightlife",
                          ].includes(id)
                        );
                        const confirmMessage = {
                          id: messages.length + 1,
                          type: "ai",
                          text: `Excellent choices! I've created your personalized Montreal itinerary focusing on ${selectedRecs.length} areas you selected. Your plan is optimized for the best experience based on your preferences.\n\nCheck the Itinerary tab to see your complete travel plan!`,
                          time: new Date().toLocaleTimeString("en-US", {
                            hour12: false,
                            hour: "2-digit",
                            minute: "2-digit",
                          }),
                        };
                        setMessages((prev) => [...prev, confirmMessage]);
                      }
                    }}
                    disabled={
                      selectedInterests.filter((id) =>
                        [
                          "cuisine",
                          "historic",
                          "shopping",
                          "nature",
                          "arts",
                          "nightlife",
                        ].includes(id)
                      ).length === 0
                    }
                  >
                    Generate Itinerary →
                  </button>
                  <button
                    style={styles.backButton2}
                    onClick={() => setInterestStep(2)}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "map" && (
          <div style={styles.mapContainer}>
            <div style={styles.mapHeader}>
              <h3 style={styles.mapTitle}>Explore Montreal</h3>
              <span style={{ fontSize: "12px", color: "#7a7a7a" }}>
                6 locations
              </span>
            </div>
            <div style={styles.mapView}>
              <div style={styles.mapControls}>
                <button style={styles.mapButton}>
                  <Plus size={16} />
                </button>
                <button style={styles.mapButton}>
                  <Minus size={16} />
                </button>
                <button style={styles.mapButton}>
                  <Maximize2 size={16} />
                </button>
                <button style={styles.mapButton}>
                  <Navigation size={16} />
                </button>
              </div>

              {mapLocations.map((location) => {
                const Icon = location.icon;
                return (
                  <div
                    key={location.id}
                    style={{
                      ...styles.mapMarker(location.type),
                      top: location.top,
                      left: location.left,
                    }}
                    onClick={() => handleMarkerClick(location)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    <Icon size={18} />
                  </div>
                );
              })}
            </div>
            <div style={styles.mapLegend}>
              <div style={styles.legendItem}>
                <span style={styles.legendDot("#667eea")}></span>
                Attractions
              </div>
              <div style={styles.legendItem}>
                <span style={styles.legendDot("#f97316")}></span>
                Dining
              </div>
              <div style={styles.legendItem}>
                <span style={styles.legendDot("#10b981")}></span>
                Parks
              </div>
              <div style={styles.legendItem}>
                <span style={styles.legendDot("#ec4899")}></span>
                Shopping
              </div>
              <div style={styles.legendItem}>
                <span style={styles.legendDot("#8b5cf6")}></span>
                Cafés
              </div>
            </div>
          </div>
        )}

        {activeTab === "itinerary" && (
          <div>
            {tripData.days && tripData.budget ? (
              <div>
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    background: "white",
                    borderRadius: "12px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#2d2d2d",
                      marginBottom: "8px",
                    }}
                  >
                    Your Montreal {tripData.days}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      fontSize: "12px",
                      color: "#7a7a7a",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#2d2d2d",
                        }}
                      >
                        {tripData.days === "3 Days"
                          ? "3"
                          : tripData.days === "2 Days"
                          ? "2"
                          : "5"}
                      </span>{" "}
                      days
                    </div>
                    <div>
                      <span
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#2d2d2d",
                        }}
                      >
                        {tripData.people === "Solo Travel"
                          ? "1"
                          : tripData.people === "2 People"
                          ? "2"
                          : "3+"}
                      </span>{" "}
                      people
                    </div>
                    <div>
                      <span
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: "#667eea",
                        }}
                      >
                        {tripData.budget === "Budget-Friendly"
                          ? "$800"
                          : tripData.budget === "Mid-Range"
                          ? "$1,500"
                          : "$3,000"}
                      </span>{" "}
                      budget
                    </div>
                  </div>
                </div>

                {/* Day-by-day itinerary */}
                <div style={{ marginBottom: "20px" }}>
                  {/* Day 1 */}
                  <div style={styles.dayCard}>
                    <div style={styles.dayHeader}>
                      <Calendar size={16} style={{ color: "#667eea" }} />
                      <span style={styles.dayTitle}>Day 1</span>
                      <span style={styles.dayLocations}>3 locations</span>
                    </div>
                    <div style={styles.dayActivities}>
                      <div style={styles.activityItem}>
                        <div style={styles.activityNumber}>1</div>
                        <div style={styles.activityDetails}>
                          <div style={styles.activityName}>
                            Old Montreal Walk
                          </div>
                          <div style={styles.activityTime}>
                            <Clock size={10} /> 09:00 • Historic District
                          </div>
                        </div>
                        <ChevronRight size={16} style={{ color: "#9a9a9a" }} />
                      </div>
                      <div style={styles.activityItem}>
                        <div style={styles.activityNumber}>2</div>
                        <div style={styles.activityDetails}>
                          <div style={styles.activityName}>
                            Notre-Dame Basilica
                          </div>
                          <div style={styles.activityTime}>
                            <Clock size={10} /> 11:00 • Gothic Architecture
                          </div>
                        </div>
                        <ChevronRight size={16} style={{ color: "#9a9a9a" }} />
                      </div>
                      <div style={styles.activityItem}>
                        <div style={styles.activityNumber}>3</div>
                        <div style={styles.activityDetails}>
                          <div style={styles.activityName}>
                            Old Port Evening
                          </div>
                          <div style={styles.activityTime}>
                            <Clock size={10} /> 15:00 • Waterfront
                          </div>
                        </div>
                        <ChevronRight size={16} style={{ color: "#9a9a9a" }} />
                      </div>
                    </div>
                  </div>

                  {/* Day 2 */}
                  <div style={styles.dayCard}>
                    <div style={styles.dayHeader}>
                      <Calendar size={16} style={{ color: "#667eea" }} />
                      <span style={styles.dayTitle}>Day 2</span>
                      <span style={styles.dayLocations}>2 locations</span>
                    </div>
                    <div style={styles.dayActivities}>
                      <div style={styles.activityItem}>
                        <div style={styles.activityNumber}>1</div>
                        <div style={styles.activityDetails}>
                          <div style={styles.activityName}>
                            Mount Royal Park
                          </div>
                          <div style={styles.activityTime}>
                            <Clock size={10} /> 09:00 • Scenic Views
                          </div>
                        </div>
                        <ChevronRight size={16} style={{ color: "#9a9a9a" }} />
                      </div>
                      <div style={styles.activityItem}>
                        <div style={styles.activityNumber}>2</div>
                        <div style={styles.activityDetails}>
                          <div style={styles.activityName}>
                            Mile End District
                          </div>
                          <div style={styles.activityTime}>
                            <Clock size={10} /> 14:00 • Cafés & Arts
                          </div>
                        </div>
                        <ChevronRight size={16} style={{ color: "#9a9a9a" }} />
                      </div>
                    </div>
                  </div>

                  {tripData.days !== "2 Days" && (
                    <div style={styles.dayCard}>
                      <div style={styles.dayHeader}>
                        <Calendar size={16} style={{ color: "#667eea" }} />
                        <span style={styles.dayTitle}>Day 3</span>
                        <span style={styles.dayLocations}>1 location</span>
                      </div>
                      <div style={styles.dayActivities}>
                        <div style={styles.activityItem}>
                          <div style={styles.activityNumber}>1</div>
                          <div style={styles.activityDetails}>
                            <div style={styles.activityName}>
                              Jean-Talon Market
                            </div>
                            <div style={styles.activityTime}>
                              <Clock size={10} /> 09:00 • Local Market
                            </div>
                          </div>
                          <ChevronRight
                            size={16}
                            style={{ color: "#9a9a9a" }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Tips */}
                <div style={styles.aiTipsCard}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <Sparkles size={20} style={{ color: "#667eea" }} />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#2d2d2d",
                      }}
                    >
                      AI Tips
                    </span>
                  </div>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      fontSize: "12px",
                      color: "#6a6a6a",
                      lineHeight: "1.6",
                    }}
                  >
                    <li>Book Notre-Dame tickets online to skip lines</li>
                    <li>Try local poutine at La Banquise (24/7)</li>
                    <li>Use BIXI bikes for easy city exploration</li>
                    <li>Visit Mount Royal at sunset for best views</li>
                  </ul>
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                  <button style={styles.saveButton}>Save Itinerary</button>
                  <button style={styles.shareButton}>Share</button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <Calendar
                  size={48}
                  style={{ color: "#e0e0e0", marginBottom: "16px" }}
                />
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#2d2d2d",
                    marginBottom: "8px",
                  }}
                >
                  No Itinerary Yet
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#7a7a7a",
                    marginBottom: "20px",
                  }}
                >
                  Start chatting with AI to create your personalized Montreal
                  itinerary
                </p>
                <button
                  style={styles.startButton}
                  onClick={() => setActiveTab("ai")}
                >
                  Start Planning
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showLocationDetails && selectedLocation && (
        <div style={styles.locationModal}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>
              <MapPin size={18} style={{ display: "inline", marginRight: 8 }} />
              {selectedLocation.name}
            </h3>
            <button
              style={styles.modalClose}
              onClick={() => setShowLocationDetails(false)}
            >
              ×
            </button>
          </div>
          <p style={styles.modalDescription}>{selectedLocation.description}</p>
          <div style={styles.modalStats}>
            <div style={styles.statItem}>
              <Star size={14} /> {selectedLocation.rating}
            </div>
            <div style={styles.statItem}>
              <DollarSign size={14} /> {selectedLocation.price}
            </div>
            <div style={styles.statItem}>
              <Clock size={14} /> {selectedLocation.hours}
            </div>
          </div>
          <div style={styles.modalActions}>
            <button style={styles.modalButton(true)}>Get Directions</button>
            <button style={styles.modalButton(false)}>Add to Itinerary</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripPlanner;
